import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Clock, Loader2, Mail, RotateCcw, Send } from "lucide-react";
import { useWallAlertSettings } from "../../lib/adminQueues";

const MODES = [
  { key: "all", label: "Every new post", hint: "Best while the walls are quiet." },
  { key: "watchlist", label: "Only posts that hit the watch list", hint: "Switch here once volume grows." },
  { key: "digest", label: "One morning summary", hint: "Once a day at the hour you pick, only on days with new posts." },
  { key: "off", label: "Off", hint: "Reports from readers still email you." },
];

const fmtHour = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** Who gets emailed when supporters post, and the words that trigger it. */
export const WallAlertSettings = () => {
  const { settings, error, save, sendDigestNow } = useWallAlertSettings();
  const [mode, setMode] = useState("all");
  const [hour, setHour] = useState(8);
  const [extras, setExtras] = useState("");
  const [words, setWords] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setMode(settings.mode);
    setHour(settings.digest_hour);
    setExtras(settings.extra_recipients.join(", "));
    setWords(settings.watch_words.join("\n"));
  }, [settings]);

  const extrasList = extras.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
  const dirty = settings && (
    mode !== settings.mode || hour !== settings.digest_hour ||
    extrasList.join(",").toLowerCase() !== settings.extra_recipients.join(",") ||
    words.trim() !== settings.watch_words.join("\n")
  );

  const onSave = async () => {
    setBusy(true);
    try {
      const out = await save({ mode, digest_hour: hour, extra_recipients: extrasList, watch_words: words.split(/[\n,]+/).map((w) => w.trim()).filter(Boolean) });
      toast.success("Wall alerts updated.", { description: `${MODES.find((m) => m.key === out.mode)?.label}${out.mode === "digest" ? ` at ${fmtHour(out.digest_hour)} Pacific` : ""} · ${1 + out.extra_recipients.length} inbox${out.extra_recipients.length ? "es" : ""} · ${out.watch_words.length} watch words.` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save alert settings.");
    } finally {
      setBusy(false);
    }
  };

  const onSendDigest = async () => {
    setSending(true);
    try {
      const r = await sendDigestNow();
      if (r.sent) toast.success(`Digest sent to ${r.to}.`, { description: `${r.count} post${r.count === 1 ? "" : "s"} included.` });
      else toast("Nothing new since the last digest.", { description: "No email was sent." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the digest.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="wall-alert-settings" className="rounded-xl border border-border bg-bone p-5 lg:p-6">
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow text-ink">
          <BellRing className="h-4 w-4" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-2xl leading-tight text-ink">Email me when supporters post</h3>
          <p className="mt-1 text-sm text-olive">
            Notices go to the alert inbox the moment a post lands, with a one-click link back here to remove it. A reader's report always sends a notice.
          </p>
          {error && <p className="mt-3 text-sm text-scarlet">{error}</p>}

          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" role="radiogroup" aria-label="Alert mode">
            {MODES.map((m) => (
              <label
                key={m.key}
                data-testid={`wall-alert-mode-${m.key}`}
                className={`cursor-pointer rounded-lg border-2 px-4 py-3 transition-colors ${mode === m.key ? "border-ink bg-yellow/10" : "border-ink/10 hover:border-ink/30"}`}
              >
                <input type="radio" name="wall-alert-mode" value={m.key} checked={mode === m.key} onChange={() => setMode(m.key)} className="sr-only" />
                <span className="block text-sm font-semibold text-ink">{m.label}</span>
                <span className="mt-0.5 block text-[12px] text-olive">{m.hint}</span>
              </label>
            ))}
          </div>

          {mode === "digest" && (
            <label className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-ink/10 bg-sand px-4 py-3" data-testid="wall-alert-digest-hour-row">
              <Clock className="h-4 w-4 text-olive" strokeWidth={2} />
              <span className="text-sm text-ink">Deliver the summary at</span>
              <select
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
                data-testid="wall-alert-digest-hour"
                className="rounded-md border border-border bg-bone px-3 py-1.5 text-sm font-semibold text-ink focus:border-ink focus:outline-none"
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>{fmtHour(h)}</option>
                ))}
              </select>
              <span className="text-sm text-olive">Pacific time, every day.</span>
            </label>
          )}

          <label className="mt-5 block" data-testid="wall-alert-recipients-row">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">Who receives alerts and digests</span>
            <span className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-[13px] font-semibold text-sand" data-testid="wall-alert-primary-recipient" title="Set by the site's ALERT_RECIPIENT_EMAIL">
                <Mail className="h-3.5 w-3.5" strokeWidth={2} /> {settings?.primary_recipient || "…"}
              </span>
              <input
                type="text"
                value={extras}
                onChange={(e) => setExtras(e.target.value)}
                placeholder="Also send to: board@example.org, another@example.org"
                data-testid="wall-alert-extra-recipients"
                className="min-w-[260px] flex-1 rounded-md border border-border bg-sand px-3 py-2 text-sm text-ink placeholder:text-olive/60 focus:border-ink focus:outline-none"
              />
            </span>
            <span className="mt-1 block text-[12px] text-olive">
              Up to {settings?.max_extra_recipients ?? 3} extra addresses, separated by commas. Each inbox gets its own copy of every alert and digest.
            </span>
          </label>

          <label className="mt-5 block">
            <span className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.2em] text-olive">
              Watch list · one word or phrase per line
              <button
                type="button"
                onClick={() => setWords((settings?.default_watch_words || []).join("\n"))}
                data-testid="wall-alert-reset-words"
                className="inline-flex items-center gap-1 normal-case tracking-normal text-ink underline-offset-2 hover:underline"
              >
                <RotateCcw className="h-3 w-3" /> Reset to starter list
              </button>
            </span>
            <textarea
              value={words}
              onChange={(e) => setWords(e.target.value)}
              rows={6}
              data-testid="wall-alert-words"
              className="mt-2 w-full rounded-md border border-border bg-sand px-3 py-2 font-mono text-[13px] leading-relaxed text-ink focus:border-ink focus:outline-none"
            />
            <span className="mt-1 block text-[12px] text-olive">
              Plain words match whole words only ("hot" won't match "hotel"); phrases and link fragments match anywhere. Matching is not case-sensitive.
            </span>
          </label>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || busy}
              data-testid="wall-alert-save"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save alert settings
            </button>
            <button
              type="button"
              onClick={onSendDigest}
              disabled={sending}
              data-testid="wall-alert-send-digest"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-ink hover:text-sand disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send digest now
            </button>
            <span className="text-[12px] text-olive" data-testid="wall-alert-digest-status">
              {settings?.last_digest_sent_at
                ? `Last digest ${new Date(settings.last_digest_sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · ${settings.last_digest_count ?? 0} post${settings.last_digest_count === 1 ? "" : "s"}`
                : "No digest sent yet"}
              {settings?.updated_at && ` · Settings changed ${new Date(settings.updated_at).toLocaleDateString()} by ${settings.updated_by}`}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
